import { useMutation, useQueryClient } from '@tanstack/react-query';
import { showErrorMessage } from '~/domain/utils';
import { useAppSelector } from '~/hooks';
import { PAYMENT_METHODS_QUERY_KEY } from '../factories';
import { paymentsService } from '../paymentsService';
import {
  IAddCardPayload,
  IConnectPlaidPayload,
  IFinalizePlaidVerifyPayload,
  IMintVerifyLinkTokenPayload,
} from '../types';
import { useBillingOffice } from './useBillingOffice';

export const paymentMutationKeys = {
  mintConnectLinkToken: ['mintConnectLinkToken'] as const,
  connectPlaid: ['connectPlaid'] as const,
  mintVerifyLinkToken: ['mintVerifyLinkToken'] as const,
  finalizePlaidVerify: ['finalizePlaidVerify'] as const,
  addCard: ['addCard'] as const,
  deletePaymentMethod: ['deletePaymentMethod'] as const,
  selectPaymentMethod: ['selectPaymentMethod'] as const,
  deleteDefaultPaymentMethod: ['deleteDefaultPaymentMethod'] as const,
};

const useInvalidatePaymentMethods = () => {
  const queryClient = useQueryClient();

  return () => queryClient.invalidateQueries({ queryKey: [PAYMENT_METHODS_QUERY_KEY] });
};

/**
 * The signed-in account and the office whose details go on a new payment method:
 * the location Billing Details was opened for (`officeId`), else the selected one.
 */
const usePaymentUserContext = (officeId?: string) => {
  const user = useAppSelector((state) => state.user.user);
  const { office: billingOffice } = useBillingOffice(officeId);

  return { user, billingOffice };
};

export const useMintConnectLinkToken = (officeId?: string) =>
  useMutation({
    mutationKey: paymentMutationKeys.mintConnectLinkToken,
    mutationFn: () => paymentsService.mintConnectLinkToken(officeId),
    onError: (error) => showErrorMessage({ error }),
  });

export const useConnectPlaid = (officeId?: string) => {
  const invalidatePaymentMethods = useInvalidatePaymentMethods();
  const { user, billingOffice } = usePaymentUserContext(officeId);

  return useMutation({
    mutationKey: paymentMutationKeys.connectPlaid,
    mutationFn: (payload: IConnectPlaidPayload) =>
      paymentsService.connectPlaid({ payload, user, billingOffice, officeId }),
    onSuccess: async () => {
      await invalidatePaymentMethods();
    },
    onError: (error) => showErrorMessage({ error }),
  });
};

export const useMintVerifyLinkToken = (officeId?: string) =>
  useMutation({
    mutationKey: paymentMutationKeys.mintVerifyLinkToken,
    mutationFn: (payload: IMintVerifyLinkTokenPayload) =>
      paymentsService.mintVerifyLinkToken(payload, officeId),
    onError: (error) => showErrorMessage({ error }),
  });

export const useFinalizePlaidVerify = (officeId?: string) => {
  const invalidatePaymentMethods = useInvalidatePaymentMethods();

  return useMutation({
    mutationKey: paymentMutationKeys.finalizePlaidVerify,
    mutationFn: (payload: IFinalizePlaidVerifyPayload) =>
      paymentsService.finalizePlaidVerify(payload, officeId),
    onSuccess: () => invalidatePaymentMethods(),
    onError: (error) => showErrorMessage({ error }),
  });
};

export const useAddCard = (officeId?: string) => {
  const invalidatePaymentMethods = useInvalidatePaymentMethods();
  const { user, billingOffice } = usePaymentUserContext(officeId);

  return useMutation({
    mutationKey: paymentMutationKeys.addCard,
    mutationFn: (payload: IAddCardPayload) =>
      paymentsService.addCard({ payload, user, billingOffice, officeId }),
    onSuccess: async () => {
      await invalidatePaymentMethods();
    },
    onError: (error) => showErrorMessage({ error }),
  });
};

export const useDeletePaymentMethod = (officeId?: string) => {
  const invalidatePaymentMethods = useInvalidatePaymentMethods();

  return useMutation({
    mutationKey: paymentMutationKeys.deletePaymentMethod,
    mutationFn: (paymentId: string) => paymentsService.deletePaymentMethod(paymentId, officeId),
    onSuccess: () => invalidatePaymentMethods(),
    onError: (error) => showErrorMessage({ error }),
  });
};

export const useSelectPaymentMethod = (officeId?: string) => {
  const invalidatePaymentMethods = useInvalidatePaymentMethods();

  return useMutation({
    mutationKey: paymentMutationKeys.selectPaymentMethod,
    mutationFn: (paymentId: string) => paymentsService.selectPaymentMethod(paymentId, officeId),
    onSuccess: async () => {
      await invalidatePaymentMethods();
    },
    onError: (error) => showErrorMessage({ error }),
  });
};

export const useDeleteDefaultPaymentMethod = (officeId?: string) => {
  const invalidatePaymentMethods = useInvalidatePaymentMethods();

  return useMutation({
    mutationKey: paymentMutationKeys.deleteDefaultPaymentMethod,
    mutationFn: async ({
      oldPaymentId,
      newPaymentId,
    }: {
      oldPaymentId: string;
      newPaymentId: string;
    }) => {
      await paymentsService.selectPaymentMethod(newPaymentId, officeId);
      await paymentsService.deletePaymentMethod(oldPaymentId, officeId);
    },
    onSuccess: async () => {
      await invalidatePaymentMethods();
    },
    onError: (error) => showErrorMessage({ error }),
  });
};
